// Apolo IDE — PlanChatPane (sprint plan-tab-v1, E5).
//
// Pane lateral direita colapsável. Conversa com o copiloto do Plan.
// Resposta com bloco ```json kind=... payload=... ``` ganha action chip
// "Criar bloco" que cria block no canvas direto.

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, Loader2, MessageSquare, Plus, Sparkles, X } from 'lucide-react';
import { usePlanChat } from '../../hooks/usePlanChat.js';

const SUGGESTIONS = [
  { label: 'Discovery', prompt: 'Liste 5 perguntas-chave de discovery pra esse plano. Para cada, sugira o tipo de bloco que melhor captura a resposta.' },
  { label: 'Arquitetura', prompt: 'Proponha o esqueleto de arquitetura técnica desse plano em blocos: heading, mermaid, texto e skills relevantes.' },
  { label: 'Próxima ação', prompt: 'Olhando os blocos atuais, qual é a próxima ação que faria esse plano avançar mais rápido?' },
  { label: 'Segurança', prompt: 'Que riscos de cyber segurança esse plano precisa considerar? Liste em blocos to-do.' },
];

function extractBlockSuggestions(content) {
  const out = [];
  const re = /```json\s*([\s\S]+?)```/g;
  let m;
  while ((m = re.exec(content || ''))) {
    try {
      const obj = JSON.parse(m[1].trim());
      if (obj && typeof obj === 'object' && obj.kind) out.push(obj);
    } catch { /* skip */ }
  }
  return out;
}

export default function PlanChatPane({ open, onClose, plan, blocks, onCreateBlock }) {
  const [draft, setDraft] = useState('');
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const { messages, sending, error, send, reset } = usePlanChat({ plan, blocks });

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  async function submit(text) {
    const txt = (text ?? draft).trim();
    if (!txt || sending) return;
    setDraft('');
    await send(txt);
  }

  const grouped = useMemo(() => messages.map((m) => ({
    ...m,
    suggestions: m.role === 'assistant' ? extractBlockSuggestions(m.content) : [],
  })), [messages]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.aside
          initial={{ x: 360, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 360, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          className="absolute right-0 top-0 z-20 flex h-full w-[360px] flex-col border-l border-apolo-borderSubtle bg-apolo-surface1"
          aria-label="Copiloto do plano"
          role="complementary"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-apolo-borderSubtle px-4 py-3">
            <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-md" style={{ background: 'var(--apolo-plan-accentMuted)', color: 'var(--apolo-plan-accent)' }}>
              <Sparkles size={14} />
            </span>
            <span className="text-sm font-semibold tracking-tight">Copiloto</span>
            <span className="ml-2 truncate text-[11px] text-apolo-textTertiary">{plan?.name}</span>
            <span className="ml-auto inline-flex items-center gap-1">
              <button type="button" onClick={reset} className="rounded-md px-2 py-1 text-[10px] text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary" aria-label="Nova conversa">
                Limpar
              </button>
              <button type="button" onClick={onClose} aria-label="Fechar copiloto" className="inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary">
                <X size={14} />
              </button>
            </span>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3" aria-live="polite">
            {grouped.length === 0 ? (
              <div className="flex flex-col gap-3 py-6 text-center">
                <span aria-hidden="true" className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full" style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}>
                  <MessageSquare size={18} className="text-white" />
                </span>
                <div>
                  <p className="text-sm font-medium text-apolo-textPrimary">Vamos planejar?</p>
                  <p className="mt-1 px-4 text-xs text-apolo-textTertiary">Peça discovery, sugira blocos, refine copy. Eu enxergo o seu canvas.</p>
                </div>
                <div className="flex flex-wrap justify-center gap-1.5 px-3">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => submit(s.prompt)}
                      disabled={sending}
                      className="rounded-full border border-apolo-plan-accentBorder bg-apolo-plan-accentMuted px-2.5 py-1 text-[11px] font-medium text-apolo-plan-accent hover:brightness-110 disabled:opacity-60"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ul className="flex flex-col gap-3">
                {grouped.map((m, idx) => (
                  <li key={idx} className={`flex flex-col gap-1 ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                    <div
                      className={`max-w-[90%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${
                        m.role === 'user'
                          ? 'bg-apolo-plan-accent text-white'
                          : 'border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textPrimary'
                      }`}
                    >
                      {m.content}
                    </div>
                    {m.suggestions?.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {m.suggestions.map((sg, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => onCreateBlock?.(sg)}
                            className="inline-flex items-center gap-1 rounded-full border border-apolo-plan-accentBorder bg-apolo-plan-accentMuted px-2.5 py-1 text-[11px] font-medium text-apolo-plan-accent hover:brightness-110"
                            title={`Criar bloco ${sg.kind}`}
                          >
                            <Plus size={10} /> Criar {sg.kind}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </li>
                ))}
                {sending ? (
                  <li className="flex items-center gap-2 text-xs text-apolo-textTertiary">
                    <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    pensando…
                  </li>
                ) : null}
              </ul>
            )}
            {error ? <p className="mt-3 text-xs text-apolo-error">{error}</p> : null}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); submit(); }}
            className="flex shrink-0 items-end gap-2 border-t border-apolo-borderSubtle px-3 py-2.5"
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              rows={1}
              placeholder="Pergunte algo… (Enter envia)"
              className="max-h-28 flex-1 resize-none rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2.5 py-2 text-sm placeholder:text-apolo-textPlaceholder focus:border-apolo-plan-accentBorder focus:outline-none focus:ring-2 focus:ring-apolo-plan-accentRing"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              aria-label="Enviar"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-plan-accentRing disabled:opacity-50"
              style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
            >
              <ArrowUp size={16} />
            </button>
          </form>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}
